import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { UserCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', company: '', plan: 'advisory_30' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [assigningCoach, setAssigningCoach] = useState(null);

  useEffect(() => { loadClients(); loadCoaches(); }, []);

  const loadClients = () => {
    axios.get(`${API}/api/admin/clients`, { withCredentials: true }).then(r => setClients(r.data)).catch(() => {});
  };

  const loadCoaches = () => {
    axios.get(`${API}/api/admin/coaches`, { withCredentials: true }).then(r => setCoaches(r.data)).catch(() => {});
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/clients`, form, { withCredentials: true });
      setForm({ email: '', password: '', name: '', company: '', plan: 'advisory_30' });
      setShowForm(false);
      loadClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignCoach = async (clientId, coachId) => {
    setAssigningCoach(clientId);
    try {
      await axios.put(`${API}/api/admin/clients/${clientId}/coach`, { coach_id: coachId || null }, { withCredentials: true });
      loadClients();
    } catch {} finally {
      setAssigningCoach(null);
    }
  };

  const planLabels = { advisory_30: '30 Min Advisory', advisory_60: '60 Min Advisory', ask_the_expert: 'Ask the Expert' };

  const getCoachName = (coachId) => {
    const coach = coaches.find(c => c.id === coachId);
    return coach ? coach.name : null;
  };

  return (
    <div data-testid="admin-clients">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 className="portal-page-title">Manage Clients</h1>
          <p className="portal-page-subtitle" style={{ margin: 0 }}>Create and manage advisory client accounts</p>
        </div>
        <button className="portal-btn portal-btn-primary" onClick={() => setShowForm(!showForm)} data-testid="add-client-btn">{showForm ? 'Cancel' : 'Add Client'}</button>
      </div>

      {showForm && (
        <div className="portal-card" style={{ marginBottom: '1.5rem' }}>
          <h3>New Client Account</h3>
          {error && <div className="portal-login-error" style={{ marginTop: '0.75rem' }}>{error}</div>}
          <form className="portal-form" style={{ marginTop: '1rem' }} onSubmit={handleCreate} data-testid="create-client-form">
            <div className="portal-grid-2">
              <div>
                <label>Full Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div>
                <label>Company</label>
                <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} />
              </div>
            </div>
            <div className="portal-grid-2">
              <div>
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div>
                <label>Temporary Password *</label>
                <input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
              </div>
            </div>
            <div>
              <label>Plan</label>
              <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                <option value="advisory_30">Advisory Retainer: 30 Min ($250/mo)</option>
                <option value="advisory_60">Advisory Retainer: 60 Min ($400/mo)</option>
                <option value="ask_the_expert">Ask the Expert ($300/session)</option>
              </select>
            </div>
            <button type="submit" className="portal-btn portal-btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create Client Account'}</button>
          </form>
        </div>
      )}

      {clients.length === 0 ? (
        <div className="portal-empty"><p>No clients yet. Add your first client to get started.</p></div>
      ) : (
        clients.map(c => (
          <div key={c.id} className="portal-card" data-testid={`client-card-${c.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0 }}>{c.name}</h3>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.88rem', color: '#5A6C8A' }}>{c.email}{c.company ? ` — ${c.company}` : ''}</p>
              </div>
              <span className="portal-badge">{planLabels[c.plan_type || c.plan] || c.plan_type || c.plan}</span>
            </div>

            {/* Coach Assignment */}
            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #F1F3F5', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <UserCheck size={16} color="#5A6C8A" />
              <span style={{ fontSize: '0.82rem', color: '#5A6C8A', fontWeight: 600, minWidth: '55px' }}>Coach:</span>
              <select
                value={c.coach_id || ''}
                onChange={(e) => handleAssignCoach(c.id, e.target.value)}
                disabled={assigningCoach === c.id}
                data-testid={`assign-coach-${c.id}`}
                style={{
                  flex: 1,
                  maxWidth: 280,
                  padding: '0.4rem 0.6rem',
                  borderRadius: 6,
                  border: '1.5px solid #E2E8F0',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '0.85rem',
                  color: c.coach_id ? '#1B2A4E' : '#9CA3AF',
                  background: assigningCoach === c.id ? '#F8F9FB' : 'white',
                }}
              >
                <option value="">No coach assigned</option>
                {coaches.map(coach => (
                  <option key={coach.id} value={coach.id}>{coach.name}</option>
                ))}
              </select>
              {c.coach_id && getCoachName(c.coach_id) && (
                <span style={{ fontSize: '0.78rem', color: '#16A34A', fontWeight: 600 }}>Assigned</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AdminClients;
